import { auth, userCollection } from "../../config/firebase";
import firebase from "firebase/app";
import "firebase/firestore";
import Cookie from "js-cookie";
import { setAuthenticating, setLoading, setMessage } from "./status";
import moment from 'moment';

export const OFFLINE_AUTH_START = 'OFFLINE_AUTH_START';
export const OFFLINE_AUTH_SUCCESS = 'OFFLINE_AUTH_SUCCESS';
export const OFFLINE_AUTH_FAILURE = 'OFFLINE_AUTH_FAILURE';
export const OFFLINE_AUTH_RESET = 'OFFLINE_AUTH_RESET';

export const SET_USER = 'SET_USER';
export const SET_PROFILE = 'SET_PROFILE';
export const SET_AUTH_DONE = 'SET_AUTH_DONE';
export const SYNC_OFFLINE_USER = 'SYNC_OFFLINE_USER';

export const syncOfflineUser = (offlineUser) => ({
    type: SYNC_OFFLINE_USER,
    payload: offlineUser
});

const generateAccessKey = () => {
    const crypto = window.crypto || window.msCrypto;
    if (crypto && crypto.getRandomValues) {
        return crypto.getRandomValues(new Uint32Array(1))[0].toString(36) + Date.now().toString(36);
    }
    return Math.random().toString(36).slice(2, 15) + Math.random().toString(36).slice(2, 15);
};

const setCookie = (name, value) => {
    const options = {
        expires: 30,
        secure: true,
        sameSite: 'strict',
        path: '/',
        domain: window.location.hostname
    };
    if (getCookie(name) !== value) {
        Cookie.set(name, value, options);
    }
};

const getCookie = name => Cookie.get(name);
const removeCookie = name => Cookie.remove(name, {
    path: '/',
    domain: window.location.hostname
});

const waitForInitialAuthState = () => new Promise(resolve => {
    let unsubscribe = () => {};
    unsubscribe = auth.onAuthStateChanged(
        user => {
            unsubscribe();
            resolve(user);
        },
        () => {
            unsubscribe();
            resolve(null);
        }
    );
});

const setAccessKey = async (uid, key) => {
    if (!uid || !key) throw new Error('Invalid parameters');
    await userCollection.doc(uid).update({
        randomString: key,
        lastAccess: firebase.firestore.FieldValue.serverTimestamp()
    });
};

const deleteAccessKey = async (uid) => {
    await userCollection.doc(uid).update({
        randomString: firebase.firestore.FieldValue.delete(),
        lastAccess: firebase.firestore.FieldValue.serverTimestamp()
    });
};

export const fetchUser = () => async (dispatch) => {
    dispatch(setAuthenticating(true));

    try {
        const user = auth.currentUser || await waitForInitialAuthState();

        if (!user) {
            dispatch({ type: SET_AUTH_DONE, payload: true });
            dispatch(setAuthenticating(false));
            return;
        }

        if (user) {
            dispatch({ type: SET_USER, payload: user });

            const userDoc = await userCollection.doc(user.uid).get();
            if (userDoc.exists) {
                const userData = userDoc.data();
                dispatch({ type: SET_PROFILE, payload: userData });

                // Sauvegarder automatiquement pour l'accès hors ligne
                try {
                    const { saveUserEmailForOffline } = await import('../../helpers/offlineUserManager');
                    const { saveUserLocally } = await import('../../helpers/saveUserLocally');

                    if (user.email) {
                        await saveUserEmailForOffline(user.email, null, {
                            uid: user.uid,
                            profile: userData,
                            displayName: user.displayName || userData.name || '',
                            photoURL: user.photoURL || '',
                            lastAutoSync: new Date().toISOString()
                        });
                        await saveUserLocally(user.email);
                    }
                } catch (syncError) {

                }
            }
        }

        dispatch({ type: SET_AUTH_DONE, payload: true });
    } catch (error) {

        dispatch(setMessage({
            message: 'Erreur lors de la récupération des données utilisateur',
            status: 'danger'
        }));
    } finally {
        dispatch(setAuthenticating(false));
    }
};

export const signIn = (email, password) => async dispatch => {
    if (!email || !password) {
        return dispatch(setMessage({
            message: 'Email et mot de passe requis',
            status: 'danger'
        }));
    }

    dispatch(setLoading(true));
    dispatch(setAuthenticating(true));
    dispatch(setMessage(null));

    try {
        await auth.setPersistence(firebase.auth.Auth.Persistence.LOCAL);
        const { user } = await auth.signInWithEmailAndPassword(email, password);

        let accessKey = getCookie('session_key');
        const isNewSession = !accessKey;

        if (isNewSession) {
            accessKey = generateAccessKey();
            setCookie('session_key', accessKey);
        }

        await setAccessKey(user.uid, accessKey);

        const unsubscribe = userCollection.doc(user.uid).onSnapshot(
            async (doc) => {
                if (!doc.exists) {
                    return;
                }
                const serverKey = doc.data().randomString;
                const localKey = getCookie('session_key');

                if (localKey && serverKey && localKey !== serverKey) {
                    await deleteAccessKey(user.uid);
                    if (window.__sessionUnsubscribe) window.__sessionUnsubscribe();
                    dispatch(signOut());
                    dispatch(setMessage({
                        message: "Votre compte a été ouvert sur un autre appareil.",
                        status: 'danger'
                    }));
                }
            },
            (error) => {

            }
        );

        window.__sessionUnsubscribe = unsubscribe;

        await dispatch(fetchUser());

        // Sauvegarder automatiquement les données utilisateur pour l'accès hors ligne
        try {
            const { saveUserEmailForOffline } = await import('../../helpers/offlineUserManager');
            const { saveUserLocally } = await import('../../helpers/saveUserLocally');

            // Sauvegarder l'email + hash du mot de passe pour la connexion hors ligne
            await saveUserEmailForOffline(email, password, {
                uid: user.uid,
                displayName: user.displayName || '',
                photoURL: user.photoURL || '',
                lastOnlineSync: new Date().toISOString()
            });
            await saveUserLocally(email);

        } catch (saveError) {

        }

        return { user };
    } catch (error) {
        let errorMessage = getAuthErrorMessage(error);

        // Gestion spéciale pour les erreurs de réseau et CORS
        if (error.code === 'auth/network-request-failed' ||
            error.message.includes('CORS') ||
            error.message.includes('Cross-Origin') ||
            error.message.includes('Failed to fetch') ||
            error.name === 'TypeError' && error.message.includes('fetch')) {

            // Vérifier si l'utilisateur existe en mode hors ligne
            try {
                const { checkOfflineUserEmail } = await import('../../helpers/offlineUserManager');
                const offlineUser = await checkOfflineUserEmail(email);

                if (offlineUser) {
                    dispatch(setMessage({
                        message: '⚠️ Problème de connexion détecté (CORS/Réseau). Basculement automatique vers le mode hors ligne...',
                        status: 'warning'
                    }));

                    // Basculer l'interface en mode hors ligne : l'utilisateur
                    // saisira son mot de passe (vérifié localement) pour se connecter.
                    setTimeout(() => {
                        window.dispatchEvent(new CustomEvent('switch-to-offline', {
                            detail: { email, hasOfflineData: true }
                        }));
                    }, 500);

                    return;
                } else {
                    errorMessage = '⚠️ Problème de connexion (CORS/Réseau). Aucune donnée hors ligne trouvée. Connectez-vous d\'abord en ligne avec une connexion stable.';
                }
                } catch (offlineError) {
                    errorMessage = '⚠️ Problème de connexion réseau. Essayez de rafraîchir la page ou vérifiez votre configuration réseau.';
                }
        }

        if (process.env.NODE_ENV === 'development') {
            errorMessage += ` (Code: ${error.code})`;
        }

        dispatch(setMessage({
            message: errorMessage,
            status: 'danger'
        }));
    } finally {
        dispatch(setLoading(false));
        dispatch(setAuthenticating(false));
    }
};

export const signOut = () => async dispatch => {
    try {
        if (window.__sessionUnsubscribe) {
            window.__sessionUnsubscribe();
        }

        const uid = auth.currentUser?.uid;
        if (uid) await deleteAccessKey(uid);

        removeCookie('session_key');

        await auth.signOut();

        dispatch({ type: SET_USER, payload: null });
        dispatch({ type: SET_PROFILE, payload: null });
        dispatch({ type: SET_AUTH_DONE, payload: false });

    } catch (error) {
        dispatch(setMessage({
            message: 'Déconnexion échouée',
            status: 'danger'
        }));
    }
};

export const fetchProfile = uid => async dispatch => {
    dispatch(setLoading(true));
    try {
        const doc = await userCollection.doc(uid).get();
        if (!doc.exists) throw new Error('Profil introuvable');

        const data = doc.data();

        const subscription = data.subscription || { status: 'none' };
        let status = 'none';

        if (subscription.endDate) {
            const expiryDate = moment(subscription.endDate.toDate ? subscription.endDate.toDate() : subscription.endDate);
            if (expiryDate.isSameOrAfter(moment(), 'day')) {
                status = 'active';
            } else {
                status = 'expired';
            }
        }

        const profileWithSubscription = {
            ...data,
            subscription: { ...subscription, status }
        };

        dispatch({ type: SET_PROFILE, payload: profileWithSubscription });
        dispatch({ type: SET_AUTH_DONE, payload: true });

        // Sauvegarder le profil pour l'accès hors ligne
        try {
            const user = auth.currentUser;
            if (user && user.email) {
                const { saveUserEmailForOffline } = await import('../../helpers/offlineUserManager');
                await saveUserEmailForOffline(user.email, null, {
                    uid: user.uid,
                    profile: profileWithSubscription,
                    displayName: user.displayName || profileWithSubscription.name || '',
                    photoURL: user.photoURL || '',
                    lastProfileSync: new Date().toISOString()
                });
            }
        } catch (saveError) {
        }

    } catch (error) {
        dispatch(setMessage({
            message: error.message.includes('introuvable') ? error.message : 'Impossible de charger le profil',
            status: 'danger'
        }));
        dispatch(signOut());
    } finally {
        dispatch(setLoading(false));
    }
};

export const registerUser = (userData) => async dispatch => {
    dispatch(setLoading(true));
    try {
        const { user } = await auth.createUserWithEmailAndPassword(userData.email, userData.password);
        const accessKey = generateAccessKey();

        await userCollection.doc(user.uid).set({
            id: user.uid,
            date: moment().add(30, 'days').format("YYYY-MM-DD"),
            subscription: {
                status: 'none',
                planName: null,
                startDate: null,
                endDate: null,
                amount: 0,
                currency: 'FCFA',
                features: []
            },
            ...userData,
            role: 'user',
            randomString: accessKey
        });

        setCookie('session_key', accessKey);

        dispatch(setMessage({
            message: 'Inscription réussie ! Votre compte a été créé avec succès. Vous pouvez maintenant vous connecter.',
            status: 'success'
        }));

        await dispatch(fetchUser());

        // Sauvegarder automatiquement le nouvel utilisateur pour l'accès hors ligne
        try {
            const { saveUserEmailForOffline } = await import('../../helpers/offlineUserManager');
            const { saveUserLocally } = await import('../../helpers/saveUserLocally');

            await saveUserEmailForOffline(userData.email, null, {
                uid: user.uid,
                profile: userData,
                displayName: user.displayName || userData.name || '',
                photoURL: user.photoURL || '',
                lastRegistrationSync: new Date().toISOString()
            });

            await saveUserLocally(userData.email);

        } catch (saveError) {

        }

    } catch (error) {
        dispatch(setMessage({
            message: getAuthErrorMessage(error),
            status: 'danger'
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

export const resetPassword = (email) => async dispatch => {
    dispatch(setLoading(true));

    try {
        await auth.sendPasswordResetEmail(email);
        dispatch(setMessage({
            message: 'Email de réinitialisation envoyé',
            status: 'success'
        }));
    } catch (error) {
        dispatch(setMessage({
            message: 'Échec de l\'envoi de l\'email',
            status: 'danger'
        }));
    } finally {
        dispatch(setLoading(false));
    }
};

const getAuthErrorMessage = (error) => {
    switch (error.code) {
        case 'auth/wrong-password':
            return 'Mot de passe incorrect. Veuillez vérifier vos identifiants.';
        case 'auth/user-not-found':
            return 'Aucun compte trouvé avec cet email. Vérifiez votre adresse email ou créez un compte.';
        case 'auth/invalid-email':
            return 'Adresse email invalide. Veuillez entrer une adresse email valide.';
        case 'auth/weak-password':
            return 'Le mot de passe doit contenir au moins 6 caractères.';
        case 'auth/email-already-in-use':
            return 'Cette adresse email est déjà utilisée. Essayez de vous connecter ou utilisez "Mot de passe oublié".';
        case 'auth/too-many-requests':
            return 'Trop de tentatives de connexion. Réessayez dans quelques minutes.';
        case 'auth/network-request-failed':
            return 'Connexion internet non disponible. Si vous vous êtes déjà connecté sur cet appareil, le mode hors ligne sera activé automatiquement.';
        case 'auth/invalid-api-key':
            return 'Erreur de configuration Firebase. Contactez l\'administrateur.';
        case 'auth/operation-not-allowed':
            return 'La méthode d\'authentification n\'est pas activée. Contactez l\'administrateur.';
        case 'auth/user-disabled':
            return 'Ce compte a été désactivé. Contactez l\'administrateur.';
        case 'auth/user-token-expired':
            return 'Votre session a expiré. Veuillez vous reconnecter.';
        case 'auth/requires-recent-login':
            return 'Cette action nécessite une connexion récente. Veuillez vous reconnecter.';
        case 'auth/account-exists-with-different-credential':
            return 'Un compte existe déjà avec cette adresse email mais avec une méthode d\'authentification différente.';
        case 'auth/credential-already-in-use':
            return 'Ces identifiants sont déjà utilisés par un autre compte.';
        case 'auth/invalid-credential':
            return 'Identifiants invalides. Veuillez vérifier vos informations.';
        case 'auth/invalid-verification-code':
            return 'Code de vérification invalide. Veuillez réessayer.';
        case 'auth/invalid-verification-id':
            return 'ID de vérification invalide. Veuillez réessayer.';
        case 'auth/quota-exceeded':
            return 'Quota dépassé. Réessayez plus tard.';
        case 'auth/app-not-authorized':
            return 'Application non autorisée. Contactez l\'administrateur.';
        case 'auth/captcha-check-failed':
            return 'Vérification CAPTCHA échouée. Veuillez réessayer.';
        case 'auth/invalid-phone-number':
            return 'Numéro de téléphone invalide.';
        case 'auth/missing-phone-number':
            return 'Numéro de téléphone requis.';
        case 'auth/invalid-recipient-email':
            return 'Adresse email de destinataire invalide.';
        case 'auth/invalid-sender':
            return 'Expéditeur invalide.';
        case 'auth/missing-iframe-start':
            return 'Erreur de configuration. Contactez l\'administrateur.';
        case 'auth/auth-domain-config-required':
            return 'Configuration du domaine d\'authentification requise. Contactez l\'administrateur.';
        case 'auth/cancelled-popup-request':
            return 'Demande de popup annulée.';
        case 'auth/popup-blocked':
            return 'Popup bloqué par le navigateur. Autorisez les popups pour ce site.';
        case 'auth/popup-closed-by-user':
            return 'Popup fermé par l\'utilisateur.';
        case 'auth/unauthorized-domain':
            return 'Domaine non autorisé. Contactez l\'administrateur.';
        case 'auth/web-storage-unsupported':
            return 'Stockage web non supporté par votre navigateur.';
        default:
            return `Erreur de connexion: ${error.message || 'Erreur inconnue'}. Veuillez réessayer.`;
    }
};